import { supabase } from './supabaseClient';

// ─────────────────────────────────────────────────────────────────────────────
// Refunds — one engine for every place money goes back to a customer.
//
//   card        Stripe refund onto a card the customer paid with here (their
//               own prior charges, newest first; the referenced invoice's
//               charges first). Never more than is still unrefunded.
//   wallet      store credit
//   cash        recorded money out
//   e_transfer  recorded money out, with a reference
//
// Card legs are created pending by the server and executed by the
// process-refunds edge function (processRefundGroup). A leg Stripe declines
// is redirected to wallet credit automatically.
// ─────────────────────────────────────────────────────────────────────────────

export type RefundDestination = 'card' | 'wallet' | 'cash' | 'e_transfer';
export type RefundSource = 'deposit' | 'manual' | 'invoice_refund' | 'invoice_void' | 'wallet_payout' | 'goodwill';

export const REFUND_DESTINATION_LABEL: Record<RefundDestination, string> = {
  card: 'Back to card',
  wallet: 'Wallet credit',
  cash: 'Cash',
  e_transfer: 'E-transfer',
};

export interface RefundCapacity {
  /** How much can go back to card right now (0 when Stripe is off). */
  cardCents: number;
  /** Number of prior charges that headroom is spread across. */
  cardPayments: number;
  stripeConnected: boolean;
  /** Customer's current wallet balance. */
  walletCents: number;
}

export async function fetchRefundCapacity(args: {
  businessId: string;
  customerId?: string | null;
  invoiceId?: string | null;
  bookingId?: string | null;
}): Promise<RefundCapacity> {
  const { data, error } = await supabase.rpc('refund_capacity', {
    p_business_id: args.businessId,
    p_customer_id: args.customerId ?? null,
    p_invoice_id: args.invoiceId ?? null,
    p_booking_id: args.bookingId ?? null,
  });
  if (error) throw error;
  return {
    cardCents: Number(data?.card_cents ?? 0),
    cardPayments: Number(data?.card_payments ?? 0),
    stripeConnected: Boolean(data?.stripe_connected),
    walletCents: Number(data?.wallet_cents ?? 0),
  };
}

export interface CardRefundLeg {
  refund_id: string;
  payment_id: string;
  stripe_payment_intent_id: string;
  amount_cents: number;
}

export interface IssueRefundResult {
  group_id: string;
  destination: RefundDestination;
  amount_cents: number;
  card_cents: number;
  wallet_cents: number;
  cash_cents: number;
  card_legs: CardRefundLeg[];
}

/** Write the refund (wallet/cash settle immediately; card legs come back pending). */
export async function issueRefund(args: {
  businessId: string;
  customerId?: string | null;
  amountCents: number;
  destination: RefundDestination;
  source: RefundSource;
  reason?: string | null;
  invoiceId?: string | null;
  bookingId?: string | null;
  containerTransactionId?: string | null;
  reference?: string | null;
}): Promise<IssueRefundResult> {
  const { data, error } = await supabase.rpc('issue_refund', {
    p_business_id: args.businessId,
    p_customer_id: args.customerId ?? null,
    p_amount_cents: args.amountCents,
    p_destination: args.destination,
    p_source: args.source,
    p_reason: args.reason ?? null,
    p_invoice_id: args.invoiceId ?? null,
    p_booking_id: args.bookingId ?? null,
    p_container_transaction_id: args.containerTransactionId ?? null,
    p_reference: args.reference ?? null,
    p_group_id: null,
  });
  if (error) throw error;
  return { ...(data as IssueRefundResult), card_legs: (data?.card_legs ?? []) as CardRefundLeg[] };
}

export interface ProcessRefundsResult {
  group_id: string;
  card_refunded_cents: number;
  /** Legs Stripe declined — already redirected to wallet credit. */
  wallet_credited_cents: number;
  legs: Array<{ refund_id: string; status: 'succeeded' | 'redirected_wallet'; amount_cents: number; stripe_refund_id?: string; error?: string }>;
}

/** Run the pending card legs of a refund request on Stripe. */
export async function processRefundGroup(groupId: string): Promise<ProcessRefundsResult> {
  const { data, error } = await supabase.functions.invoke('process-refunds', { body: { groupId } });
  if (error) {
    let msg = error.message;
    try {
      const ctx = await (error as { context?: Response }).context?.json();
      if (ctx?.error) msg = ctx.error;
    } catch { /* keep original message */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return {
    group_id: data?.group_id ?? groupId,
    card_refunded_cents: Number(data?.card_refunded_cents ?? 0),
    wallet_credited_cents: Number(data?.wallet_credited_cents ?? 0),
    legs: (data?.legs ?? []) as ProcessRefundsResult['legs'],
  };
}

/**
 * Issue + (for card) execute in one call. Returns where the money actually
 * ended up after Stripe had its say.
 */
export async function refundCustomer(args: Parameters<typeof issueRefund>[0]): Promise<RefundOutcome> {
  const issued = await issueRefund(args);
  let cardCents = 0;
  let walletCents = issued.wallet_cents;
  if (issued.card_legs.length > 0) {
    const processed = await processRefundGroup(issued.group_id);
    cardCents = processed.card_refunded_cents;
    walletCents += processed.wallet_credited_cents;
  }
  return {
    groupId: issued.group_id,
    destination: args.destination,
    amountCents: issued.amount_cents,
    cardCents,
    walletCents,
    cashCents: issued.cash_cents,
  };
}

export interface RefundOutcome {
  groupId: string;
  destination: RefundDestination;
  amountCents: number;
  cardCents: number;
  walletCents: number;
  cashCents: number;
}

/** Human summary of where a refund ended up ("$25.00 back to card · $5.00 to wallet"). */
export function describeRefundOutcome(o: Pick<RefundOutcome, 'cardCents' | 'walletCents' | 'cashCents'> & { destination?: RefundDestination }): string {
  const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;
  const parts: string[] = [];
  if (o.cardCents > 0) parts.push(`${fmt(o.cardCents)} back to card`);
  if (o.walletCents > 0) parts.push(`${fmt(o.walletCents)} to wallet`);
  if (o.cashCents > 0) parts.push(`${fmt(o.cashCents)} ${o.destination === 'e_transfer' ? 'by e-transfer' : 'in cash'}`);
  return parts.join(' · ') || 'Nothing refunded';
}

/** After a delivery / counter checkout: execute any card legs it created. */
export async function finishCardRefundLegs(result: {
  refund_group_id?: string | null;
  card_refund_legs?: CardRefundLeg[] | null;
}): Promise<ProcessRefundsResult | null> {
  if (!result.refund_group_id || !result.card_refund_legs || result.card_refund_legs.length === 0) return null;
  return processRefundGroup(result.refund_group_id);
}
